import { type AdminUserRowDto } from '@icebreaker/shared';
import { type TranslationKey, type Translator } from '@icebreaker/shared/i18n';
import { Avatar, Modal, Tag } from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import styles from './admin.module.css';

export interface UserDetailDialogProps {
  readonly user: AdminUserRowDto | null;
  readonly roleLabel: string;
  readonly statusLabel: string;
  readonly statusVariant: 'outline' | 'danger' | 'warning';
  readonly onClose: () => void;
}

/**
 * KULLANICI DETAYI (tasarım ekran 25, satır eylemi "Detay").
 *
 * Referans düğmeyi ÇİZER ama açtığı katmanı çizmez; bu yüzden katman panelin
 * kendi karar panosu desenini (kicker + olgu satırları + denetim notu) izler.
 *
 * UYDURMA ALAN YOK (CLAUDE.md md. 9): satırlar YALNIZCA `AdminUserRowDto`nun
 * taşıdığı alanlardan doğar. Ucu olmayan bir alan (askı gerekçesi, oturum
 * geçmişi) boş bir satırla ya da tahmin sayıyla DOLDURULMAZ — hiç çizilmez.
 */
export function UserDetailDialog({
  user,
  roleLabel,
  statusLabel,
  statusVariant,
  onClose,
}: UserDetailDialogProps) {
  const t = useTranslation();
  if (!user) return null;

  const rows = detailRows(t, user);

  return (
    <Modal
      open
      kicker={t.t('admin.users.detailKicker')}
      title={user.displayName}
      closeLabel={t.t('common.close')}
      onClose={onClose}
    >
      <div className={styles.detailPerson}>
        <Avatar name={user.displayName} size="md" />
        <span className={styles.detailPersonText}>
          <span className={styles.personName}>{user.displayName}</span>
          <span className={styles.personMail}>{user.email}</span>
        </span>
        <Tag variant={statusVariant}>{statusLabel}</Tag>
      </div>

      <div className={styles.factList}>
        <div className={styles.factRow}>
          <span className={styles.factKey}>{t.t('admin.users.detailFields.role')}</span>
          <span className={styles.factValue}>{roleLabel}</span>
        </div>
        {rows.map((row) => (
          <div key={row.key} className={styles.factRow}>
            <span className={styles.factKey}>
              {t.t(`admin.users.detailFields.${row.key}` as TranslationKey)}
            </span>
            <span className={styles.factValue}>{row.value}</span>
          </div>
        ))}
      </div>

      <div className={styles.auditNote}>{t.t('admin.users.detailNote')}</div>
    </Modal>
  );
}

/** Satır listesi: değeri OLMAYAN alan listeye hiç girmez. */
function detailRows(
  t: Translator,
  user: AdminUserRowDto,
): readonly { key: string; value: string }[] {
  const rows: { key: string; value: string }[] = [
    { key: 'email', value: user.email },
    { key: 'events', value: t.formatNumber(user.eventCount) },
    {
      key: 'wallet',
      value: user.isOrganizer ? '—' : t.formatNumber(user.walletCount),
    },
    {
      key: 'lastSeen',
      value: user.lastSeenAt
        ? t.formatDate(user.lastSeenAt, { dateStyle: 'medium', timeStyle: 'short' })
        : t.t('admin.users.neverSeen'),
    },
    { key: 'registered', value: t.formatDate(user.createdAt, { dateStyle: 'medium' }) },
  ];
  if (user.suspendedAt) {
    rows.push({
      key: 'suspendedAt',
      value: t.formatDate(user.suspendedAt, { dateStyle: 'medium' }),
    });
  }
  return rows;
}
